/**
 * EditorialHeatmap — the count grid used by the BPA workload view and the App
 * Posture permission view, as a Carbon table on layer-01 (D13): the cell
 * intensity is a neutral alpha ramp of the primary ink (l0 none, l1-l4
 * stronger), and only the critical top bucket (`l4` with `crit`) takes Coral,
 * as a tint and the count's ink.
 * The number in each cell carries the reading; the ramp only orders it.
 * Square cells, 1px border-subtle rules, no rounding or shadow.
 *
 * The grid scrolls sideways inside its own box, so it never widens the page.
 * Non-zero cells become buttons when `onCellClick` is set.
 */

import type { CSSProperties, ReactNode } from 'react'
import clsx from 'clsx'

export type Intensity = 'l0' | 'l1' | 'l2' | 'l3' | 'l4'

export interface HeatmapCell {
  value: number | string
  intensity: Intensity
  /** When intensity = l4 and crit = true, render the Coral critical treatment. */
  crit?: boolean
  /** Optional sub-label rendered below the value (e.g. units). */
  hint?: string
}

export interface HeatmapRow {
  /** Row label, e.g. "Identity". */
  label: string
  /** Optional second line, e.g. "42 controls". */
  sublabel?: string
  cells: HeatmapCell[]
  /** When provided, an extra "Health" column is rendered with the score and
   *  a 7d delta ("78% · +3 in 7 days"). */
  health?: { score: number; delta: number | null }
  /** Optional click target for the row. */
  href?: string
}

interface EditorialHeatmapProps {
  rows: HeatmapRow[]
  columns: string[]
  /** First column width in px. Default 200. */
  labelWidth?: number
  /** Override the empty-state node. */
  emptyState?: ReactNode
  /** When provided, non-zero cells become buttons that fire this callback. */
  onCellClick?: (rowIdx: number, colIdx: number, row: HeatmapRow, cell: HeatmapCell) => void
  /** The first column's header. Default "Workload". */
  labelHeader?: string
  /** The trailing score column's header. Default "Health". */
  healthHeader?: string
  /** The table's accessible name. */
  ariaLabel?: string
}

// The neutral ramp is the D13 heatmap tokens (index.css), the primary ink at
// .08/.14/.22/.32 alpha dark and .05/.09/.14/.20 light. The token scale counts
// from the faintest filled step, so it sits one below this component's levels
// (an empty cell takes no fill). The top step stays light enough for primary
// text to read on it.
const INTENSITY_STYLE: Record<Intensity, CSSProperties> = {
  l0: {},
  l1: { background: 'var(--heatmap-l0-bg)' },
  l2: { background: 'var(--heatmap-l1-bg)' },
  l3: { background: 'var(--heatmap-l2-bg)' },
  l4: { background: 'var(--heatmap-l3-bg)' },
}

// The critical top bucket (D13): a Coral tint (`--heatmap-l4-bg`, .28 alpha
// dark, .18 light), never a solid block, with the count in Coral ink at 600,
// as the side nav sets critical counts. At 20px/600 the light ink clears the
// large-text contrast bar on the tint; the column header names the severity.
const CRIT_STYLE: CSSProperties = { background: 'var(--heatmap-l4-bg)', color: 'var(--heatmap-l4-fg)' }

const CELL = 'h-14 min-w-[5.5rem] border-l border-t border-line-subtle p-0 text-center align-middle'
const HEAD = 'h-12 whitespace-nowrap bg-layer-accent px-4 text-body font-semibold text-text-primary'

function deltaText(delta: number | null): string {
  if (delta == null) return 'No 7-day change yet'
  if (delta === 0) return 'No change in 7 days'
  return `${delta > 0 ? '+' : '−'}${Math.abs(delta)} in 7 days`
}

export default function EditorialHeatmap({
  rows,
  columns,
  labelWidth = 200,
  emptyState,
  onCellClick,
  labelHeader = 'Workload',
  healthHeader = 'Health',
  ariaLabel,
}: EditorialHeatmapProps) {
  if (rows.length === 0) {
    return (
      <div className="bg-layer-1 px-4 py-8 text-center text-helper text-text-helper">
        {emptyState ?? 'No data to chart yet.'}
      </div>
    )
  }

  const hasHealth = rows.some(r => r.health != null)

  return (
    <div className="min-w-0 overflow-x-auto bg-layer-1">
      <table aria-label={ariaLabel} className="w-full border-collapse">
        <thead>
          <tr>
            <th scope="col" className={clsx(HEAD, 'text-left')} style={{ width: labelWidth }}>
              {labelHeader}
            </th>
            {columns.map(c => (
              <th key={c} scope="col" className={clsx(HEAD, 'text-center')}>
                {c}
              </th>
            ))}
            {hasHealth && (
              <th scope="col" className={clsx(HEAD, 'text-right')}>
                {healthHeader}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`${row.label}-${idx}`}>
              <th scope="row" className="border-t border-line-subtle px-4 py-2 text-left align-middle font-normal">
                <span className="block text-body font-semibold text-text-primary">{row.label}</span>
                {row.sublabel && <span className="block text-helper text-text-helper">{row.sublabel}</span>}
              </th>

              {row.cells.map((cell, ci) => {
                const crit = cell.intensity === 'l4' && cell.crit
                const numericValue =
                  typeof cell.value === 'number'
                    ? cell.value
                    : Number.isFinite(Number(cell.value))
                      ? Number(cell.value)
                      : NaN
                const isClickable = onCellClick != null && Number.isFinite(numericValue) && numericValue > 0
                const inner = (
                  <>
                    <span className={clsx('block text-heading tabular-nums', crit ? 'font-semibold' : 'font-light text-text-primary')}>
                      {cell.value}
                    </span>
                    {cell.hint && (
                      <span className={clsx('block text-helper', crit ? '' : 'text-text-secondary')}>{cell.hint}</span>
                    )}
                  </>
                )
                return (
                  <td key={ci} className={CELL} style={crit ? CRIT_STYLE : INTENSITY_STYLE[cell.intensity]}>
                    {isClickable ? (
                      <button
                        type="button"
                        onClick={() => onCellClick!(idx, ci, row, cell)}
                        aria-label={`Open ${row.label} ${columns[ci]}, ${cell.value}, in the deep dive`}
                        className="grid h-full min-h-14 w-full place-items-center content-center px-3 py-2 transition-colors duration-110 ease-carbon hover:bg-layer-hover"
                      >
                        {inner}
                      </button>
                    ) : (
                      <span className="grid min-h-14 place-items-center content-center px-3 py-2">{inner}</span>
                    )}
                  </td>
                )
              })}

              {hasHealth && (
                <td className="border-t border-line-subtle px-4 py-2 text-right align-middle">
                  {row.health ? (
                    <>
                      <span className="block text-heading font-light tabular-nums text-text-primary">
                        {Math.round(row.health.score)}%
                      </span>
                      <span className="block whitespace-nowrap text-helper tabular-nums text-text-helper">
                        {deltaText(row.health.delta)}
                      </span>
                    </>
                  ) : (
                    <span className="text-helper text-text-helper">No score yet</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
